import React, { useContext, useEffect, useMemo, useState } from "react"
import { Link } from "gatsby"

import { Locale } from "../../context"
import { PropertyCard } from "../../components/utils/PropertyCard"

const TYPESENSE_COLLECTION_PREFIX =
  process.env.GATSBY_TYPESENSE_COLLECTION_PREFIX || "properties"

// "Buy" property status. Similar listings must always point to AVAILABLE
// properties — especially on a Sold page, where showing other sold properties
// would waste the visitor. We recover them onto real, purchasable alternatives.
const AVAILABLE_STATUS_ID = "110023778"

const escapeFilterValue = value =>
  String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/`/g, "\\`")

const getTypesenseCollectionName = locale =>
  `${TYPESENSE_COLLECTION_PREFIX}_${locale}`

const getImageUrl = hit => {
  if (hit?.hero_image_url) return hit.hero_image_url
  if (Array.isArray(hit?.gallery_urls) && hit.gallery_urls[0]) {
    return hit.gallery_urls[0]
  }
  return ""
}

const toPropertyCardShape = hit => {
  if (!hit) return null

  const galleryUrls = Array.isArray(hit.gallery_urls)
    ? hit.gallery_urls.filter(Boolean)
    : []
  const galleryAlts = Array.isArray(hit.gallery_alts) ? hit.gallery_alts : []
  const gallery =
    galleryUrls.length > 0
      ? galleryUrls.map((url, index) => ({
          url,
          smartTags: galleryAlts[index] ? [galleryAlts[index]] : [],
        }))
      : getImageUrl(hit)
      ? [
          {
            url: getImageUrl(hit),
            smartTags: hit.hero_image_alt ? [hit.hero_image_alt] : [],
          },
        ]
      : []

  return {
    source: "typesense",
    reference: hit.reference || hit.id,
    buyerEligibility: hit.buyer_eligibility || "",
    title: hit.title || hit.name || hit.reference || "",
    slug: hit.slug || hit.url_slug || "",
    description: hit.description || "",
    price: hit.price,
    hidePrice: hit.hide_price ?? hit.hidePrice,
    surface: hit.surface,
    land: hit.land,
    bedrooms: hit.bedrooms,
    bathrooms: hit.bathrooms,
    currency: { code: hit.currency_code || hit.currency?.code || "EUR" },
    propertyStatus: {
      name: hit.property_status_name,
      originalId: hit.property_status_original_id,
      _allNameLocales: [
        { locale: "fr", value: hit.property_status_name },
        { locale: "en", value: hit.property_status_name },
      ],
    },
    propertyType: {
      name: hit.property_type_name,
      _allNameLocales: [
        { locale: "fr", value: hit.property_type_name },
        { locale: "en", value: hit.property_type_name },
      ],
    },
    city: {
      name: hit.city_name,
      _allNameLocales: [
        { locale: "fr", value: hit.city_name },
        { locale: "en", value: hit.city_name },
      ],
      country: {
        name: hit.country_name,
        _allNameLocales: [
          { locale: "fr", value: hit.country_name },
          { locale: "en", value: hit.country_name },
        ],
      },
    },
    gallery,
    _allTitleLocales: [
      { locale: "fr", value: hit.title || hit.name || hit.reference || "" },
      { locale: "en", value: hit.title || hit.name || hit.reference || "" },
    ],
    _allSlugLocales: [
      { locale: "fr", value: hit.slug || hit.url_slug || "" },
      { locale: "en", value: hit.slug || hit.url_slug || "" },
    ],
  }
}

const mergeUniqueResults = (current, incoming) => {
  const seen = new Set(
    current.map(item => item.reference || item.id || item.slug)
  )
  const merged = [...current]
  incoming.forEach(item => {
    const key = item.reference || item.id || item.slug
    if (!key || seen.has(key)) return
    seen.add(key)
    merged.push(item)
  })
  return merged
}

const getLocationLabel = (property, locale) => {
  const city = property?.city?.name
  const country = property?.city?.country?.name
  if (city && country) return `${city}, ${country}`
  if (city) return city
  if (country) return country
  return locale === "fr" ? "cette destination" : "this destination"
}

const getDestinationHref = (property, locale) => {
  const country = String(property?.city?.country?.name || "").toLowerCase()
  const isFr = locale === "fr"
  if (country.includes("spain") || country.includes("espagne")) {
    return isFr ? "/fr/proprietes-marbella/" : "/en/properties-marbella/"
  }
  if (country.includes("mauritius") || country.includes("maurice")) {
    return isFr ? "/fr/proprietes-ile-maurice/" : "/en/properties-mauritius/"
  }
  if (
    country.includes("emirats") ||
    country.includes("emirates") ||
    country.includes("dubai")
  ) {
    return isFr ? "/fr/proprietes-dubai/" : "/en/properties-dubai/"
  }
  if (country.includes("france")) {
    return isFr ? "/fr/proprietes-france/" : "/en/properties-france/"
  }
  if (
    country.includes("greece") ||
    country.includes("grèce") ||
    country.includes("grece")
  ) {
    return isFr ? "/fr/proprietes-grece/" : "/en/properties-greece/"
  }
  if (country.includes("maldives")) {
    return isFr ? "/fr/proprietes-maldives/" : "/en/properties-maldives/"
  }
  if (
    country.includes("united states") ||
    country.includes("états") ||
    country.includes("etats")
  ) {
    return isFr ? "/fr/proprietes-etats-unis/" : "/en/properties-united-states/"
  }
  return isFr ? "/fr/recherche/" : "/en/search/"
}

const Similar = ({
  property,
  eyebrow,
  title,
  description,
  sectionClassName = "bg-white py-12 md:py-16 lg:py-20",
  innerClassName = "mx-auto max-w-[1120px] border-t border-black/10 pt-10 md:pt-14",
}) => {
  // Same trap as the hero badge: the context is empty at build time, so the
  // headings and the call to action rendered in English on French pages.
  const { locale: contextLocale } = useContext(Locale)
  const locale = property?.locale || contextLocale || "en"
  const [results, setResults] = useState([])
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(true)

  const filterBy = useMemo(() => {
    const clauses = []
    if (property?.city?.name) {
      clauses.push(`city_name:=\`${escapeFilterValue(property.city.name)}\``)
    }
    // Always surface AVAILABLE alternatives (never the current property's own
    // status — a Sold page must recommend properties that can still be bought).
    clauses.push(`property_status_original_id:=\`${AVAILABLE_STATUS_ID}\``)
    if (property?.propertyType?.name) {
      clauses.push(
        `property_type_name:=\`${escapeFilterValue(
          property.propertyType.name
        )}\``
      )
    }
    if (Number.isFinite(Number(property?.price)) && Number(property.price) > 0) {
      clauses.push(`price:>=${Math.round(Number(property.price) * 0.9)}`)
    }
    if (property?.reference) {
      clauses.push(`reference:!=\`${escapeFilterValue(property.reference)}\``)
    }
    return clauses.join(" && ")
  }, [property])

  const fallbackFilters = useMemo(() => {
    const referenceClause = property?.reference
      ? `reference:!=\`${escapeFilterValue(property.reference)}\``
      : ""
    const cityClause = property?.city?.name
      ? `city_name:=\`${escapeFilterValue(property.city.name)}\``
      : ""
    const countryClause = property?.city?.country?.name
      ? `country_name:=\`${escapeFilterValue(property.city.country.name)}\``
      : ""
    const statusClause = `property_status_original_id:=\`${AVAILABLE_STATUS_ID}\``
    const typeClause = property?.propertyType?.name
      ? `property_type_name:=\`${escapeFilterValue(property.propertyType.name)}\``
      : ""

    return [
      [cityClause, statusClause, typeClause, referenceClause],
      [cityClause, statusClause, referenceClause],
      [cityClause, typeClause, referenceClause],
      [cityClause, referenceClause],
      [countryClause, statusClause, typeClause, referenceClause],
      [countryClause, statusClause, referenceClause],
      [countryClause, typeClause, referenceClause],
      [countryClause, referenceClause],
      [statusClause, typeClause, referenceClause],
      [statusClause, referenceClause],
      [referenceClause],
    ]
      .map(clauses => clauses.filter(Boolean).join(" && "))
      .filter(Boolean)
  }, [property])

  useEffect(() => {
    if (
      !process.env.GATSBY_TYPESENSE_HOST ||
      !process.env.GATSBY_TYPESENSE_SEARCH_API_KEY ||
      !filterBy
    ) {
      setLoading(false)
      return undefined
    }

    let cancelled = false
    const controller = new AbortController()

    const run = async () => {
      setLoading(true)
      setError("")
      try {
        const buildEndpoint = nextFilterBy => {
          const params = new URLSearchParams({
            q: "*",
            query_by:
              "reference,title,name,estate,city_name,country_name,property_type_name,property_status_name",
            filter_by: nextFilterBy,
            per_page: "8",
          })
          const protocol = process.env.GATSBY_TYPESENSE_PROTOCOL || "https"
          const port = process.env.GATSBY_TYPESENSE_PORT || "443"
          const path = process.env.GATSBY_TYPESENSE_PATH || ""
          const collection = getTypesenseCollectionName(locale)
          return `${protocol}://${process.env.GATSBY_TYPESENSE_HOST}:${port}${path}/collections/${collection}/documents/search?${params.toString()}`
        }

        const fetchResults = async nextFilterBy => {
          if (!nextFilterBy) return []
          const response = await fetch(buildEndpoint(nextFilterBy), {
            headers: {
              "X-TYPESENSE-API-KEY": process.env.GATSBY_TYPESENSE_SEARCH_API_KEY,
            },
            signal: controller.signal,
          })
          if (!response.ok) throw new Error(`Typesense ${response.status}`)
          const data = await response.json()
          return (data.hits || []).map(hit => hit.document || hit)
        }

        let nextResults = await fetchResults(filterBy)
        for (const nextFilterBy of fallbackFilters) {
          if (nextResults.length >= 3) break
          const fallbackResults = await fetchResults(nextFilterBy)
          nextResults = mergeUniqueResults(nextResults, fallbackResults)
        }

        if (!cancelled) {
          setResults(nextResults.slice(0, 3))
        }
      } catch (err) {
        if (cancelled || err.name === "AbortError") return
        setError(err.message || "Search failed")
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    run()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [fallbackFilters, filterBy, locale])

  const isFr = locale === "fr"
  const locationLabel = getLocationLabel(property, locale)
  const destinationHref = getDestinationHref(property, locale)
  const hasResults = !error && results.length > 0
  // On a sold (or otherwise unavailable) listing, reframe the section to recover
  // the visitor onto available alternatives instead of a generic "you may like".
  const currentIsAvailable =
    property?.propertyStatus?.originalId === AVAILABLE_STATUS_ID
  const eyebrowText =
    eyebrow ||
    (currentIsAvailable
      ? isFr
        ? "Vous aimerez aussi"
        : "You may also like"
      : isFr
      ? "Ce bien est vendu"
      : "This property is sold")
  const titleText =
    title ||
    (currentIsAvailable
      ? isFr
        ? `Biens similaires a ${locationLabel}`
        : `Similar properties in ${locationLabel}`
      : isFr
      ? `Biens disponibles a ${locationLabel}`
      : `Available properties in ${locationLabel}`)
  const descriptionText =
    description ||
    (currentIsAvailable
      ? isFr
        ? "Une selection courte pour comparer le style, l'emplacement et le niveau de presentation sans quitter votre recherche."
        : "A concise shortlist to compare style, location and positioning without leaving the flow of your search."
      : isFr
      ? "Ce bien vient d'etre vendu. Voici une selection de proprietes disponibles, comparables en emplacement et en standing."
      : "This property has just been sold. Here is a selection of available properties, comparable in location and positioning.")
  const cardResults = useMemo(
    () => results.map(toPropertyCardShape).filter(Boolean),
    [results]
  )

  return (
    <section
      id="available-properties"
      className={sectionClassName}
      style={{ scrollMarginTop: "96px" }}
    >
      <div className="container">
        <div className={innerClassName}>
          <div className="mb-7 grid gap-5 md:mb-10 md:grid-cols-[minmax(0,1fr)_minmax(300px,0.48fr)] md:items-end">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#86868b]">
                {eyebrowText}
              </p>
              <h2 className="mt-3 max-w-3xl text-[32px] font-semibold leading-[1.05] text-[#1d1d1f] md:text-[48px]">
                {titleText}
              </h2>
            </div>
            <p className="max-w-md text-[15px] leading-[1.65] text-[#6e6e73] md:justify-self-end md:text-[16px]">
              {descriptionText}
            </p>
          </div>

          {hasResults ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {cardResults.map((similarProperty, index) => (
                <PropertyCard
                  key={similarProperty.reference || similarProperty.slug}
                  property={similarProperty}
                  variant="listing"
                  sliderDisabled
                  priority={index === 0}
                />
              ))}
            </div>
          ) : loading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map(item => (
                <div
                  key={item}
                  className="aspect-[4/3] animate-pulse rounded-[18px] bg-[#f5f5f7]"
                />
              ))}
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_360px] md:items-stretch">
              <Link
                to={destinationHref}
                className="group overflow-hidden rounded-[8px] border border-black/10 bg-[#f5f5f7] p-6 transition duration-300 hover:-translate-y-0.5 hover:bg-[#f0f0f2] md:p-8"
              >
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
                  {isFr ? "Collection destination" : "Destination collection"}
                </p>
                <p className="mt-4 max-w-2xl text-[28px] font-semibold leading-[1.08] text-[#1d1d1f] md:text-[40px]">
                  {isFr
                    ? `Voir plus de biens a ${locationLabel}`
                    : `View more properties in ${locationLabel}`}
                </p>
                <span className="mt-6 inline-flex h-11 items-center rounded-full bg-[#0071e3] px-5 text-[15px] font-medium text-white transition group-hover:bg-[#0077ed]">
                  {isFr ? "Explorer la collection" : "Explore collection"}
                </span>
              </Link>

              <button
                type="button"
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.dispatchEvent(
                      new CustomEvent("property:open-lead-form")
                    )
                  }
                }}
                className="rounded-[8px] border border-black/10 bg-white p-6 text-left shadow-[0_12px_34px_rgba(15,23,42,0.055)] transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_48px_rgba(15,23,42,0.09)] md:p-8"
              >
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
                  {isFr ? "Selection privee" : "Private shortlist"}
                </p>
                <p className="mt-4 text-[24px] font-semibold leading-[1.12] text-[#1d1d1f]">
                  {isFr
                    ? "Recevoir des alternatives similaires"
                    : "Receive similar alternatives"}
                </p>
                <p className="mt-4 text-[15px] leading-[1.6] text-[#6e6e73]">
                  {isFr
                    ? "Notre equipe peut vous envoyer une selection ciblee avec les biens les plus proches."
                    : "Our team can send a focused shortlist with the closest matching properties."}
                </p>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default Similar
